import { useCallback, useEffect, useRef, useState } from 'react';

import { Alert, Badge, Input, message, Modal, Space, Tabs, Typography } from 'antd';

import { deleteTransitionConfig } from '../../../api/Delete';
import { getTransitionJob, getTransitionPlatforms } from '../../../api/Get';
import { startDeployCode, startReloadClasses, startTransition } from '../../../api/Post';
import { putTransitionConfig } from '../../../api/Put';

import ClassesPanel from './ClassesPanel';
import ConfigPanel from './ConfigPanel';
import JobStatus from './JobStatus';
import ResultPanel from './ResultPanel';
import SeriesPanel from './SeriesPanel';
import { POLL_INTERVAL_MS } from './shared';
import TransitionPanel from './TransitionPanel';

const { Paragraph, Text, Title } = Typography;

/*
 * Page des opérations Edifice de fin d'année. Quatre opérations indépendantes, une seule
 * pouvant tourner à la fois côté serveur : elles partagent donc un même suivi.
 *
 * Chacune a son onglet, et le résultat a le sien : autrefois tout s'empilait sur une seule
 * page et le rapport finissait sous plusieurs écrans de commandes.
 */
const TransitionAnnee = () => {
  const [platforms, setPlatforms] = useState([]);
  const [availableCodes, setAvailableCodes] = useState([]);
  const [config, setConfig] = useState([]);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('transition');

  const [selected, setSelected] = useState([]);
  const [newCode, setNewCode] = useState(null);
  const [oldCode, setOldCode] = useState(null);
  // `null` = toutes les plateformes configurées avec ce code ; sinon, la sélection choisie.
  const [codePlatforms, setCodePlatforms] = useState(null);
  const [classPlatforms, setClassPlatforms] = useState([]);
  const [onlyStale, setOnlyStale] = useState(true);

  const [savingConfig, setSavingConfig] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [now, setNow] = useState(Date.now());
  const [messageApi, contextHolder] = message.useMessage();

  const pollRef = useRef(null);

  const running = job && job.status === 'running';
  // `alive` n'est renseigné que par une réponse du serveur : un job tout juste lancé
  // localement ne doit pas être signalé en panne.
  const stalled = running && job.alive === false;

  /* Signatures : une exécution n'est proposée qu'après une simulation portant sur la même
     chose. Elles doivent reproduire exactement celles calculées côté serveur. */
  const simulatedOk = (kind, signature) =>
    job && job.dryRun && job.status === 'done' && (job.kind || 'transition') === kind && job.signature === signature;

  const codeOptions = availableCodes.map(c => ({
    label: `${c.code} — ${c.episodes} épisode(s), ${c.utilisateurs} détenteur(s)${c.description ? ` · ${c.description}` : ''}`,
    value: c.code,
  }));

  const knownPlatforms = [...new Set([...platforms.map(p => p.projet), ...config.map(c => c.projet)])]
    .filter(p => p !== '*')
    .sort();

  // Sans sélection, une plateforme est visée si sa configuration désigne le code à poser.
  const autoTargeted = platforms.filter(p => p.code === newCode).map(p => p.projet);
  const targetedPlatforms = codePlatforms || autoTargeted;

  const codeSignature = `${oldCode || ''}>${newCode || ''}@${codePlatforms ? [...codePlatforms].sort().join(',') : 'auto'}`;
  const classSignature = `${onlyStale ? 'retard' : 'tous'}@${[...classPlatforms].sort().join(',')}`;

  const loadPlatforms = useCallback(async () => {
    const res = await getTransitionPlatforms();
    if (res.error) setError(res.error);
    else {
      setPlatforms(res.platforms);
      setAvailableCodes(res.codes || []);
      setConfig(res.config || []);
      // Proposition par défaut : le code configuré pour les nouveaux enseignants.
      const parDefaut = (res.config || []).find(c => c.projet === '*');
      setNewCode(current => current ?? parDefaut?.code ?? null);
    }
  }, []);

  useEffect(() => {
    loadPlatforms();
    return () => window.clearInterval(pollRef.current);
  }, [loadPlatforms]);

  // Fait vivre le compteur « serveur actif il y a X s » entre deux interrogations.
  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  const follow = jobId => {
    window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      const res = await getTransitionJob(jobId);
      if (res.error) {
        window.clearInterval(pollRef.current);
        setError(res.error);
        return;
      }
      setJob(res);
      if (res.status !== 'running') {
        window.clearInterval(pollRef.current);
        if (res.status === 'error') setError(res.error);
        else {
          messageApi.open({ content: res.dryRun ? 'Simulation terminée' : 'Opération terminée', type: 'success' });
          // Le rapport est ce qu'on veut lire tout de suite : on y amène l'opérateur.
          if (res.report) setTab('resultat');
        }
      }
    }, POLL_INTERVAL_MS);
  };

  const launchJob = async (kind, dryRun, confirmValue) => {
    setError('');
    setJob(null);

    const calls = {
      classes: () => startReloadClasses({ confirm: confirmValue, dryRun, onlyStale, platforms: classPlatforms }),
      code: () => startDeployCode({ confirm: confirmValue, dryRun, newCode, oldCode, platforms: codePlatforms }),
      transition: () => startTransition({ confirm: confirmValue, dryRun, platforms: selected }),
    };

    const res = await calls[kind]();
    const shown = { classes: classPlatforms, code: targetedPlatforms, transition: selected }[kind];
    const pending = { dryRun, id: res.jobId, kind, platforms: shown, status: 'running' };

    if (res.error) {
      setError(res.error);
      // Une opération déjà en cours a pu être lancée depuis l'autre serveur : on se
      // raccroche à son suivi plutôt que de laisser l'opérateur sans rien.
      if (res.jobId) {
        setJob(pending);
        follow(res.jobId);
      }
      return;
    }
    setJob(pending);
    follow(res.jobId);
  };

  const onConfirm = async () => {
    const { kind } = confirmFor;
    const value = confirmText;
    setConfirmFor(null);
    setConfirmText('');
    await launchJob(kind, false, value);
  };

  const saveConfig = async row => {
    setSavingConfig(row.projet);
    const res = await putTransitionConfig(row);
    setSavingConfig(null);
    if (res.error) setError(res.error);
    else {
      messageApi.open({ content: `Configuration de ${row.projet} enregistrée`, type: 'success' });
      loadPlatforms();
    }
  };

  const dropConfig = async projet => {
    const res = await deleteTransitionConfig(projet);
    if (res.error) setError(res.error);
    else {
      messageApi.open({ content: `Surcharge de ${projet} supprimée`, type: 'success' });
      loadPlatforms();
    }
  };

  const tabs = [
    {
      children: (
        <TransitionPanel
          askConfirm={setConfirmFor}
          job={job}
          launch={dryRun => launchJob('transition', dryRun, undefined)}
          platforms={platforms}
          running={running}
          selected={selected}
          setSelected={setSelected}
          simulationOk={simulatedOk('transition', [...selected].sort().join(','))}
        />
      ),
      key: 'transition',
      label: 'Transition d’année',
    },
    {
      children: (
        <SeriesPanel
          askConfirm={setConfirmFor}
          autoTargeted={autoTargeted}
          codeOptions={codeOptions}
          codePlatforms={codePlatforms}
          job={job}
          knownPlatforms={knownPlatforms}
          launch={dryRun => launchJob('code', dryRun, undefined)}
          newCode={newCode}
          oldCode={oldCode}
          running={running}
          setCodePlatforms={setCodePlatforms}
          setNewCode={setNewCode}
          setOldCode={setOldCode}
          simulationOk={Boolean(newCode) && simulatedOk('code', codeSignature)}
          targetedPlatforms={targetedPlatforms}
        />
      ),
      key: 'series',
      label: 'Sélection de séries',
    },
    {
      children: (
        <ClassesPanel
          askConfirm={setConfirmFor}
          classPlatforms={classPlatforms}
          job={job}
          knownPlatforms={knownPlatforms}
          launch={dryRun => launchJob('classes', dryRun, undefined)}
          onlyStale={onlyStale}
          running={running}
          setClassPlatforms={setClassPlatforms}
          setOnlyStale={setOnlyStale}
          simulationOk={classPlatforms.length > 0 && simulatedOk('classes', classSignature)}
        />
      ),
      key: 'classes',
      label: 'Classes',
    },
    {
      children: (
        <ConfigPanel
          codeOptions={codeOptions}
          config={config}
          dropConfig={dropConfig}
          platforms={platforms}
          running={running}
          saveConfig={saveConfig}
          savingConfig={savingConfig}
        />
      ),
      key: 'config',
      label: 'Configuration',
    },
    {
      children: <ResultPanel job={job} />,
      key: 'resultat',
      label: job && job.report ? <Badge dot>Résultat</Badge> : 'Résultat',
    },
  ];

  return (
    <>
      {contextHolder}
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space direction="vertical" size={0}>
          <Title level={4} style={{ marginBottom: 0 }}>
            Edifice — opérations de fin d’année
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Quatre opérations indépendantes, chacune avec sa simulation. Une seule peut tourner à la fois.
          </Paragraph>
        </Space>

        {error && <Alert message={error} showIcon type="error" onClose={() => setError('')} closable />}

        {/* Le suivi reste en haut : on ne descend pas chercher où en est l'opération. */}
        {running && <JobStatus job={job} now={now} stalled={stalled} />}

        <Tabs activeKey={tab} items={tabs} onChange={setTab} />
      </Space>

      <Modal
        open={Boolean(confirmFor)}
        okButtonProps={{ danger: true, disabled: !confirmFor || confirmText !== confirmFor.expected }}
        okText="Exécuter"
        cancelText="Annuler"
        title={confirmFor ? confirmFor.title : ''}
        onCancel={() => {
          setConfirmFor(null);
          setConfirmText('');
        }}
        onOk={onConfirm}>
        <Paragraph>{confirmFor ? confirmFor.description : ''}</Paragraph>
        <Paragraph>
          Pour confirmer, saisir :{' '}
          <Text code copyable>
            {confirmFor ? confirmFor.expected : ''}
          </Text>
        </Paragraph>
        <Input
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          placeholder={confirmFor ? confirmFor.expected : ''}
        />
      </Modal>
    </>
  );
};

export default TransitionAnnee;
